/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { getRouteInitData, queryFormEditable, State } from '@gh/common-shared';
import {
  createFilteredDataSource,
  FilteredDataSource,
  mapSlicedDataItems,
  SlicedDataSource,
} from '@gh/core-data';
import { GridInfiniteScroll, RowPointer } from '@gh/core-ui-grid';
import { skip$ } from '@gh/rx/operators';

import { AgGridEvent } from 'ag-grid-community';
import { isNil } from 'lodash';
import { Observable, Subscription } from 'rxjs';

import { PriceBookDetailsBusinessService } from '../../../resources/price-book-details-business.service';
import {
  getPbHousePlanId,
  housePlanToRowPointer,
  mergeDirtyHousePlan,
  PbHousePlan,
  PbHousePlanFilter,
} from '../../../resources/price-book.model';
import { PbCommandService } from '../pb-command.service';
import {
  PbDetailsState,
  queryDirtyHousePlans,
  queryHousePlan,
  queryHousePlanFilter,
  queryHousePlanRequest,
  queryPbSelectedHousePlan,
} from '../pb-details.state';
import { PbDetailsHpGridDefinitionService } from './pb-details-hp-grid-definition.service';

@Component({
  standalone: false,
  selector: 'gh-pb-details-house-plan-list',
  templateUrl: 'pb-details-hp-list.component.html',
  styleUrls: ['pb-details-hp-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [PbDetailsHpGridDefinitionService],
})
export class PbDetailsHpListComponent implements OnInit, OnDestroy {
  filter$: Observable<PbHousePlanFilter>;
  selectedHousePlan$: Observable<Maybe<PbHousePlan>>;
  dataSource: SlicedDataSource<PbHousePlan>;
  finderDataSource: FilteredDataSource<RowPointer, string>;
  editable: boolean;

  @ViewChild('infiniteScroll', { static: false }) infiniteScroll: GridInfiniteScroll;

  private filterSubscription: Subscription;
  private dirtyDataSubscription: Subscription;
  private dataLoadedEventTriggeredOnce: boolean = false;

  constructor(private route: ActivatedRoute,
              private changeDetectorRef: ChangeDetectorRef,
              public gridDefinition: PbDetailsHpGridDefinitionService,
              private businessService: PriceBookDetailsBusinessService,
              private state: State<PbDetailsState>,
              private commands: PbCommandService) {

  }

  ngOnInit(): void {
    const { priceBookHousePlansGridLayout } = getRouteInitData(this.route);

    this.gridDefinition.bind(priceBookHousePlansGridLayout);

    this.editable = this.state.project(queryFormEditable);
    this.filter$ = this.state.select(queryHousePlanFilter);
    this.selectedHousePlan$ = this.state.select(queryPbSelectedHousePlan);

    this.filterSubscription = this.filter$
      .subscribe(() => {
        // construct data source for grid. This data source will merge data coming from the backend
        // and data stored in the current working session
        this.dataSource = mapSlicedDataItems(
          createFilteredDataSource(
            () => this.businessService.findRelatedHousePlansAsDataSource(this.state.project(queryHousePlanRequest))),
          (hp) => mergeDirtyHousePlan(hp, this.state.project(queryHousePlan(getPbHousePlanId(hp)))));
        // construct data source for grid finder. This data source will filter data by text filter
        this.finderDataSource = mapSlicedDataItems(
          createFilteredDataSource(
            (filter: string) => this.businessService.findRelatedHousePlansAsDataSource({
              ...this.state.project(queryHousePlanRequest),
              searchString: filter,
            })),
          housePlanToRowPointer);

        this.changeDetectorRef.markForCheck();
      });

    // refresh grid content when some house plan data were changed
    this.dirtyDataSubscription = this.state.select(queryDirtyHousePlans).pipe(
      skip$(1))
      .subscribe(() => this.infiniteScroll.refresh());
  }

  ngOnDestroy(): void {
    this.filterSubscription.unsubscribe();
    this.dirtyDataSubscription.unsubscribe();
  }

  onAdd(): void {
    this.commands.addHousePlans();
  }

  onRemove(hp: PbHousePlan): void {
    this.commands.removeHousePlan(hp);
  }

  onUpdate(hp: PbHousePlan): void {
    this.commands.updateHousePlan(hp);
  }

  onFilterChange(field: keyof PbHousePlanFilter, value: any): void {
    this.commands.updateHpFilter(field, value);
  }

  onRowSelected(hp: PbHousePlan): void {
    this.commands.selectHousePlan(hp);
  }

  onHousePlanUpdateComplete(event: AgGridEvent): void {
    if (this.dataLoadedEventTriggeredOnce) {
      return;
    }
    this.dataLoadedEventTriggeredOnce = true;

    const api = event.api;
    if (!api) {
      return;
    }

    const trySelectFirst = () => {
      if (!isNil(this.state.project(queryPbSelectedHousePlan)) || api.getDisplayedRowCount() <= 0) {
        return true;
      }
      const first = api.getDisplayedRowAtIndex(0);
      if (first?.data != null && first.stub !== true) {
        first.setSelected(true);
        return true;
      }
      return false;
    };

    if (trySelectFirst()) {
      return;
    }

    const onModelUpdated: () => void = () => {
      if (trySelectFirst()) {
        api.removeEventListener('modelUpdated', onModelUpdated);
      }
    };
    api.addEventListener('modelUpdated', onModelUpdated);
  }
}
